import type {
	CertificationPreview,
	CourseAccreditationByProfession,
	CourseCategoryShallow,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import useSWR, { type SWRResponse } from "swr";

import CoursesAccreditationSelect from "./CoursesAccreditationSelect";
import CoursesCatalogueItem from "./CoursesCatalogueItem";
import Accordion from "~/components/Accordion";
import CertificationSelect from "~/pages/certifications/[certificationSlug]/components/CertificationSelect";

interface CoursesCatalogueProps {
	certifications: CertificationPreview[];
	shallowCategories: CourseCategoryShallow[];
	accreditationsByProfession: CourseAccreditationByProfession[];
}

const CoursesCatalogue: React.FC<CoursesCatalogueProps> = ({
	certifications,
	shallowCategories,
	accreditationsByProfession,
}) => {
	const { userId } = useAuth();
	const { data: passedCredits } = useSWR<number>(
		userId && "/courses/credits/passed",
	);
	const { data: totalCredits } = useSWR<number>(
		"/courses/credits/total",
	) as SWRResponse<number>;

	return (
		<div className="relative flex flex-col gap-5">
			<div className="relative flex items-center justify-between">
				<CertificationSelect certifications={certifications} />

				<div
					className={clsx(
						"relative shrink-0 overflow-hidden rounded-xl border px-3 py-1 font-medium dark:bg-slate-700",
					)}
				>
					<div className="relative dark:text-slate-50">
						<span className="mr-1">
							{passedCredits} {passedCredits !== undefined && " / "}
							{totalCredits}
						</span>
						Credits
					</div>
				</div>
			</div>

			{accreditationsByProfession && (
				<CoursesAccreditationSelect
					accreditationsByProfession={accreditationsByProfession}
				/>
			)}

			{shallowCategories && (
				<div className="flex flex-col gap-2">
					{shallowCategories?.map((category) => (
						<Accordion key={category.slug}>
							<CoursesCatalogueItem category={category} />
						</Accordion>
					))}
				</div>
			)}
		</div>
	);
};

export default CoursesCatalogue;
